import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { toAppError, type AppError } from '../lib/errors'

/**
 * Team-domains hooks.
 *
 * Post-2026-05-16 the team-domains surface is read-mostly from the client:
 *
 *   - Domain rows themselves are created by the signup trigger (the first
 *     corporate user on a domain claims it). There is no client-facing
 *     `add_team_domain` RPC anymore; out-of-band operator action is the
 *     escape hatch for domain edits.
 *   - The only writable knob is the team-level `auto_join_by_domain`
 *     boolean, controlled by `set_team_auto_join` (owner-only).
 *
 * `mode` on a domain row is gone — the toggle moved up to the team.
 *
 * Types are hand-written; the new `set_team_auto_join` RPC isn't in
 * `database.types.ts` yet (regenerated post-merge). Same `unknown`-cast
 * pattern used by the sibling team hooks.
 */

type UntypedClient = {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>
  from: (
    table: string,
  ) => {
    select: (cols: string) => {
      eq: (col: string, val: string) => {
        order: (
          col: string,
          opts: { ascending: boolean },
        ) => Promise<{ data: unknown; error: { message: string } | null }>
      }
    }
  }
}

const untyped = (): UntypedClient => supabase as unknown as UntypedClient

/**
 * Domain row. `mode` is intentionally absent — it lived in the 2026-05-14
 * shape and was removed by 2026-05-16. The team-level flag replaces it.
 */
export interface TeamDomainRow {
  id: string
  team_id: string
  domain: string
  added_by: string | null
  created_at: string
}

export const teamDomainsKey = (teamId: string | undefined): QueryKey =>
  ['teamDomains', teamId ?? 'none']

// ──────────────────────────────────────────────────────────────────────────
// List domains for a team (read-only)
// ──────────────────────────────────────────────────────────────────────────

export function useTeamDomains(teamId: string | null | undefined) {
  const key = teamDomainsKey(teamId ?? undefined)
  const query = useQuery<TeamDomainRow[]>({
    queryKey: key,
    enabled: !!teamId,
    queryFn: async () => {
      const { data, error } = await untyped()
        .from('team_domains')
        .select('id, team_id, domain, added_by, created_at')
        .eq('team_id', teamId!)
        .order('created_at', { ascending: true })
      if (error) throw new Error(error.message)
      return (data as TeamDomainRow[] | null) ?? []
    },
  })
  return {
    domains: query.data ?? [],
    loading: !!teamId && query.isPending,
    error: query.error ? toAppError(query.error) : null as AppError | null,
  }
}

// ──────────────────────────────────────────────────────────────────────────
// set_team_auto_join — owner-only team-level toggle
// ──────────────────────────────────────────────────────────────────────────

export interface SetTeamAutoJoinArgs {
  enabled: boolean
}

/**
 * Toggle the team's `auto_join_by_domain` boolean. Owner-only at the RPC
 * layer — the UI mirrors that gate (renders the toggle only when isOwner).
 *
 * Invalidates the broad `['team', teamId]` key so `useTeam` / `useTeams`
 * re-fetch and UI that reads `team.auto_join_by_domain` sees the new
 * value immediately.
 *
 * Types note: `set_team_auto_join` isn't in `database.types.ts` yet.
 * Same `unknown`-cast pattern as `useSetTeamMemberRole` in useTeamMembers.
 */
export function useSetTeamAutoJoin(teamId: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation<void, Error, SetTeamAutoJoinArgs>({
    mutationKey: ['teamDomains', 'setAutoJoin', teamId],
    mutationFn: async ({ enabled }) => {
      if (!teamId) throw new Error('No team')
      const { error } = await untyped().rpc('set_team_auto_join', {
        p_team_id: teamId,
        p_enabled: enabled,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      // Broad invalidate — the toggle affects `useTeams` (which hydrates
      // team rows) and anywhere consuming the flag directly.
      qc.invalidateQueries({ queryKey: ['team', teamId] })
      qc.invalidateQueries({ queryKey: ['teams'] })
    },
  })
}
